import { useState } from 'react'
import { FileText, Image as ImageIcon, ImageOff, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { isOrderItemImageMedia, resolveOrderItemMediaUrl } from '@/lib/order-item-media'
import { formatDate } from '@/lib/utils'
import type { StoreOrderItemMedia } from '@/types'

// Order item media in the job media tab's card look (components/jobs/job-media-gallery-editor.tsx).

const formatFileSize = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) {
    return null
  }
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Stored as "<unique id>_<original name>" behind a signed URL; show the original name.
export function orderItemMediaDisplayName(media: Pick<StoreOrderItemMedia, 'path' | 'type_str' | 'mime'>): string {
  const lastPart = String(media.path ?? '').split('?')[0].split('/').at(-1) ?? ''
  let name = lastPart
  try {
    name = decodeURIComponent(lastPart)
  } catch {
    // keep the raw segment
  }
  return name.replace(/^\d+_/, '') || media.type_str || media.mime || 'Media'
}

interface OrderItemMediaCardProps {
  media: StoreOrderItemMedia
  onRemove?: () => void
  removeDisabled?: boolean
  timeZone?: string | null
}

export function OrderItemMediaCard({ media, onRemove, removeDisabled, timeZone }: OrderItemMediaCardProps) {
  const [imageFailed, setImageFailed] = useState(false)
  const source = resolveOrderItemMediaUrl(media.path)
  const isImage = isOrderItemImageMedia(media)
  const name = orderItemMediaDisplayName(media)
  const extension = name.includes('.') ? name.split('.').at(-1)?.toUpperCase() : null
  const meta = [
    formatFileSize(media.file_size),
    media.created_on ? formatDate(media.created_on, timeZone ? { timeZone } : undefined) : null,
  ].filter(Boolean).join(' • ')

  let preview
  if (isImage && source && !imageFailed) {
    preview = (
      <img
        src={source}
        alt={name}
        className="h-36 w-full rounded-xl object-cover"
        loading="lazy"
        onError={() => setImageFailed(true)}
      />
    )
  } else if (isImage) {
    preview = (
      <div className="flex h-36 flex-col items-center justify-center gap-2 rounded-xl bg-muted/40 text-muted-foreground">
        <ImageOff className="h-8 w-8" />
        <span className="text-xs">Preview unavailable</span>
      </div>
    )
  } else if (source) {
    preview = (
      <a
        href={source}
        target="_blank"
        rel="noreferrer"
        className="flex h-36 flex-col items-center justify-center rounded-xl bg-muted/40 p-4 text-center transition-colors hover:bg-muted"
      >
        <FileText className="mb-3 h-10 w-10 text-muted-foreground" />
        <span className="line-clamp-2 text-sm font-medium">{name}</span>
      </a>
    )
  } else {
    preview = (
      <div className="flex h-36 items-center justify-center rounded-xl bg-muted/40">
        <FileText className="h-10 w-10 text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
      <div className="p-3">{preview}</div>
      <div className="space-y-3 px-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-medium" title={name}>{name}</p>
            {meta ? <p className="text-xs text-muted-foreground">{meta}</p> : null}
          </div>
          {extension ? <Badge variant="secondary">{extension}</Badge> : null}
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {isImage ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
            <span>{isImage ? 'Image' : 'Document'}</span>
          </div>
          <div className="flex items-center gap-2">
            {source ? (
              <Button variant="outline" size="sm" asChild>
                <a href={source} target="_blank" rel="noreferrer">
                  Open
                </a>
              </Button>
            ) : null}
            {onRemove ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-destructive"
                onClick={onRemove}
                disabled={removeDisabled}
                aria-label={`Delete ${name}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
